import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import PlayerActions, { type PlayerAvailableActions } from 'components/tables/players/PlayerActions';
import { Feature, featureEnabled } from 'config/clients';
import { getEnvConfig } from 'config/environment';
import { useAddToEventDialog } from 'containers/dialogs/AddToEvent';
import { useAddToTeamDialog } from 'containers/dialogs/AddToTeam';
import { usePenaltyDialog } from 'containers/dialogs/Penalty';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { useEmailDialog } from 'containers/dialogs/SendEmail';
import { type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import type { ParticipantInterface } from 'typedefs/store/tmxTypes';
import { noop } from 'utils/fnUtils';

type PlayerPopoverContext = (
  participant?: ParticipantInterface,
  anchorEl?: HTMLElement,
  options?: { matchUp?: MatchUpInterface },
) => void;

const context = createContext<PlayerPopoverContext>(noop);

export const usePlayerPopover = () => useContext(context);

export const useAvailableActions = () => {
  return useCallback(getAvailableActions, []);
};

export const getAvailableActions = (participant: ParticipantInterface): PlayerAvailableActions => {
  return {
    PROFILE: !!(participant?.participantId && featureEnabled(Feature.PLAYER_PROFILES)),
    PENALTY: !!featureEnabled(Feature.SUSPENSIONS),
    EVENT: true,
    TEAM: true,
    EMAIL: getEnvConfig().REACT_APP_DISABLE_CUSTOM_EMAIL !== 'true',
  };
};

const PlayerPopover: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [participant, setParticipant] = useState<ParticipantInterface>();
  const [matchUp, setMatchUp] = useState<MatchUpInterface>();
  const [playerActions, setPlayerActions] = useState<PlayerAvailableActions>({});
  const anchorEl = useRef<HTMLElement | null>();
  const [open, setOpen] = useState<boolean>(false);
  const showProfileDialog = usePlayersProfileDialog();
  const showPenaltyDialog = usePenaltyDialog();
  const showAddToEventDialog = useAddToEventDialog();
  const showAddToTeamDialog = useAddToTeamDialog();
  const getAvailableActions = useAvailableActions();
  const showEmailDialog = useEmailDialog();

  const handleOpenPlayersProfile = useCallback(() => {
    showProfileDialog([{ participantId: participant.participantId, participantName: participant.participantName }]);
    setOpen(false);
  }, [participant, showProfileDialog]);

  const handleSuspensionPointAction = useCallback(() => {
    showPenaltyDialog({ participants: [participant], matchUp });
    setOpen(false);
  }, [matchUp, participant, showPenaltyDialog]);

  const handleEventAction = useCallback(() => {
    showAddToEventDialog([
      {
        ...participant,
        participantId: participant.participantId,
        penalties: [],
        participantType: null,
      },
    ]);
    setOpen(false);
  }, [participant, showAddToEventDialog]);

  const handleTeamAction = useCallback(() => {
    showAddToTeamDialog([participant], true);
    setOpen(false);
  }, [participant, showAddToTeamDialog]);

  const handleEmailAction = useCallback(() => {
    showEmailDialog([participant]);
    setOpen(false);
  }, [participant, showEmailDialog]);

  const contextValue = useCallback<PlayerPopoverContext>(
    (participant, target, options) => {
      setParticipant(participant);
      setMatchUp(options?.matchUp);
      setPlayerActions(getAvailableActions(participant));
      setOpen(true);
      anchorEl.current = target;
    },
    [getAvailableActions],
  );

  const handleClose = useCallback(() => setOpen(false), []);

  return useMemo(
    () => (
      <context.Provider value={contextValue}>
        {children}
        <PlayerActions
          actions={playerActions}
          anchorEl={() => anchorEl.current}
          open={open}
          onClose={handleClose}
          onEmailAction={handleEmailAction}
          onEventAction={handleEventAction}
          onOpenPlayersProfile={handleOpenPlayersProfile}
          onSuspensionPointAction={handleSuspensionPointAction}
          onTeamAction={handleTeamAction}
        />
      </context.Provider>
    ),
    [
      children,
      contextValue,
      handleClose,
      handleEventAction,
      handleOpenPlayersProfile,
      handleSuspensionPointAction,
      handleTeamAction,
      open,
      playerActions,
      handleEmailAction,
    ],
  );
};

export default PlayerPopover;
